import {
	ChangeDetectionStrategy,
	Component,
	Input,
	OnDestroy,
	OnInit,
} from '@angular/core';
import { openClose } from '@core/animations/animations';
import {
	clientLegalName,
	isBuyer,
	isGuarantor,
	isOfferor,
} from '@core/util/client';
import { ICON } from '@core/util/icon';
import {
	ClientCreationData,
	ClientType,
	PersonClient,
	Client,
	BusinessClient,
} from '@models/clients';
import {
	BusinessMatterClientWithAssociations,
	MatterClientType,
	MatterFileType,
	PersonMatterClientWithAssociations,
	SpousalStatus,
} from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { MatterClients } from '@state/global/matter_clients';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { BusinessState } from '@state/business';
import { NameFormattingSettings } from '@models/businessSettings/settings';
import { YES_NO_OPTIONS } from 'src/app/ui/utils/constants';
import { SpouseKebabOptions } from '../../../../sale/parties/components/client-tab/client-tab.component';
import { BaseComponent } from '@core/base.component';
import { GlobalProfileComponent } from '../../../global-profile.component';
import { Associations } from '../../../global-profile.types';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import { MatterClientDispatch } from '@state/matter_clients/matterClients.dispatch';
import { MatterClientsState } from '@state/matter_clients';
import { SearchType } from 'src/app/public/leap/leap-api.service';
import { TestHooks } from './client-tab-profile.hooks';

interface SpousalSelection {
	value: SpousalStatus;
	name: string;
	clientId?: string;
}

const SpousalText = {
	[SpousalStatus.NOT_A_SPOUSE]: 'Not a spouse',
	[SpousalStatus.CONSENTING]: 'Spouse is consenting to transaction',
	[SpousalStatus.PROPERTY_NOT_MATRIMONIAL_HOME]:
		'Property is not a matrimonial home',
	[SpousalStatus.CLIENT_SEPARATED_NOT_MATRIMONIAL_HOME]:
		'Client is separated from spouse and property was not family residence at time of separation',
	[SpousalStatus.CLIENT_SEPARATED_SPOUSE_RELEASE_RIGHTS]:
		"Client's spouse has released all rights under separation agreement",
	[SpousalStatus.COURT_AUTH_TRANSACTION]:
		'Transaction is authorized by a court order',
	[SpousalStatus.PROPERTY_NOT_OCCUPIED]:
		'Client is not separated, and the property is not occupied as the family residence',
};

const OtherSpouseOptions = [
	{
		value: SpousalStatus.CONSENTING,
		name: SpousalText[SpousalStatus.CONSENTING],
	},
	{
		value: SpousalStatus.PROPERTY_NOT_OCCUPIED,
		name: SpousalText[SpousalStatus.PROPERTY_NOT_OCCUPIED],
	},
	{
		value: SpousalStatus.CLIENT_SEPARATED_SPOUSE_RELEASE_RIGHTS,
		name: SpousalText[SpousalStatus.CLIENT_SEPARATED_SPOUSE_RELEASE_RIGHTS],
	},
	{
		value: SpousalStatus.COURT_AUTH_TRANSACTION,
		name: SpousalText[SpousalStatus.COURT_AUTH_TRANSACTION],
	},
	{
		value: SpousalStatus.CLIENT_SEPARATED_NOT_MATRIMONIAL_HOME,
		name: SpousalText[SpousalStatus.CLIENT_SEPARATED_NOT_MATRIMONIAL_HOME],
	},
	{
		value: SpousalStatus.PROPERTY_NOT_MATRIMONIAL_HOME,
		name: SpousalText[SpousalStatus.PROPERTY_NOT_MATRIMONIAL_HOME],
	},
];

enum ClientKebabOptionTypes {
	GLOBALVIEW = 'globalView',
	REMOVE = 'remove',
	PRIMARY = 'primary',
}

export interface VM {
	_client:
		| PersonMatterClientWithAssociations
		| BusinessMatterClientWithAssociations;
	guarantorMortgagesNames: string[];
	spousalGroupItems1: SpousalSelection[];
	selectedSpouseStatus: string;
	selectedSpouseText: string;
	spousalGroupItems2: SpousalSelection[];
	allClients: (
		| PersonMatterClientWithAssociations
		| BusinessMatterClientWithAssociations
	)[];
	showOfferor: boolean;
	showPurchaseOptions: boolean;
	isPurchaser: boolean;
	isGuarantor: boolean;
	flaClient: PersonClient;
	checkboxProcessing: boolean;
}

export const defaultVM: VM = {
	_client: null,
	guarantorMortgagesNames: [],
	spousalGroupItems1: [
		{
			value: SpousalStatus.NOT_A_SPOUSE,
			name: SpousalText[SpousalStatus.NOT_A_SPOUSE],
		},
	],
	spousalGroupItems2: OtherSpouseOptions,
	selectedSpouseStatus: '',
	selectedSpouseText: '',
	allClients: [],
	showOfferor: false,
	showPurchaseOptions: false,
	isPurchaser: false,
	isGuarantor: false,
	flaClient: null,
	checkboxProcessing: false,
};

@UntilDestroy()
@Component({
	selector: 'app-client-tab-profile',
	templateUrl: './client-tab-profile.component.html',
	animations: [openClose()],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class ClientTabProfileComponent
	extends BaseComponent<VM>
	implements OnInit, OnDestroy
{
	testHooks = TestHooks;
	public clientTypes = ClientType;
	public ICON = ICON;
	public spousalStatuses = SpousalStatus;
	public spouseKebabOptions = SpouseKebabOptions;
	public fileTypes = MatterFileType;
	public yesNoOptions = YES_NO_OPTIONS;
	private clientTypesToShowPurchaseOptions = [
		MatterClientType.BUYER,
		MatterClientType.OFFEROR,
		MatterClientType.GUARANTOR,
	];

	@SelectSnapshot(BusinessState.getNameFormattingSettings)
	nameFormattingSettings: NameFormattingSettings;

	@Select(MatterClients.get_allMatterClientsWithAssociationMapped)
	allClients$: Observable<
		(
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[]
	>;

	@ViewSelectSnapshot(SelectedMatter.matterFileType) fileType: MatterFileType;
	@Select(MatterMortgagesState.getMortgagesNamesByGuarantor)
	mortgagesNames$: Observable<(clientId: string) => string[]>;
	@SelectSnapshot(SelectedMatter.matterFileType) matterFileType: MatterFileType;

	@SelectSnapshot(SelectedMatter.isLeapMatter) isLeapMatter: boolean;

	@Input() client: PersonClient | BusinessClient;
	@Input() associations: Associations;

	constructor(
		private matterClientDispatch: MatterClientDispatch,
		private drawerService: DrawerService,
		private matterClientsState: MatterClientsState,
	) {
		super(defaultVM);
	}
	public SearchType = SearchType;

	ngOnInit(): void {
		this.allClients$.pipe(untilDestroyed(this)).subscribe((_allClients) => {
			const currentClient = _allClients.find((x) => x.id === this.client.id);

			if (currentClient) {
				const updatedVM = {};
				this.getGuarantorMortgagesNames(currentClient?.id).subscribe(
					(mortgagesNames) => {
						updatedVM['guarantorMortgagesNames'] = mortgagesNames;
					},
				);
				updatedVM['showOfferor'] = isOfferor(currentClient);
				updatedVM['isPurchaser'] =
					isBuyer(currentClient) || isGuarantor(currentClient);
				updatedVM['isGuarantor'] = isGuarantor(currentClient);
				updatedVM['allClients'] = _allClients;
				updatedVM['showPurchaseOptions'] = currentClient.clientTypes?.some(
					(x) => this.clientTypesToShowPurchaseOptions.includes(x),
				);

				this.VM = {
					...updatedVM,
					_client: currentClient,
					allClients: _allClients,
					spousalGroupItems1: this.getSpousalItems(
						currentClient as PersonClient,
						_allClients,
					),
					selectedSpouseText: this.getSelectedSpouseText(
						currentClient?.fla?.clientId,
						currentClient?.fla?.spousalStatus,
						(currentClient as PersonMatterClientWithAssociations)?.flaClient,
						_allClients,
					),
					selectedSpouseStatus: currentClient?.fla?.spousalStatus,
					flaClient: (currentClient as PersonMatterClientWithAssociations)
						?.flaClient,
				};
			}
		});
	}

	ngOnDestroy(): void {
		if (this.VM?._client?.offeror === '' && this.VM?.showOfferor) {
			this.matterClientDispatch.SetOfferorName(
				this.VM._client.id,
				clientLegalName(this.VM._client, this.nameFormattingSettings),
			);
		}
	}

	setInstrumentNumber(instrumentNumber: string): void {
		this.matterClientDispatch.SetSpousalFLAInstrumentId(
			this.VM._client.id,
			instrumentNumber,
		);
	}

	changeFLA($event: DropdownResult<SpousalSelection>): void {
		if (this.VM.selectedSpouseStatus === $event.result.value) return;

		this.VM = {
			selectedSpouseStatus: $event.result.value,
			selectedSpouseText: $event.result.name,
		};
		if (
			$event.result.value === SpousalStatus.CONSENTING &&
			this.associations.spousal.length > 0
		) {
			const spousalClientId = this.isLeapMatter
				? this.associations.spousal[0].clientInfo.leap?.cardId
				: this.associations.spousal[0].clientInfo.id;

			this.matterClientDispatch.SetSpousalClientId(
				this.VM._client.id,
				spousalClientId,
				true,
				$event.result.value,
			);
		} else {
			this.matterClientDispatch.SetSpousalStatus(
				this.VM._client?.id,
				$event.result.value,
				$event.result.clientId,
			);
		}

		if ($event.result.value === SpousalStatus.SPOUSE_TO) {
			this.matterClientDispatch.SetSpousalStatus(
				$event.result.clientId,
				$event.result.value,
				this.VM._client.id,
			);
		}
	}

	getGuarantorMortgagesNames(clientId: string): Observable<string[]> {
		return this.mortgagesNames$.pipe(
			untilDestroyed(this),
			map((fltr) => fltr(clientId)),
		);
	}

	setOfferor(offerorName: string) {
		this.matterClientDispatch.SetOfferorName(this.VM._client.id, offerorName);
	}

	setFirstTimeHomeBuyer(checked: boolean) {
		this.matterClientDispatch.SetAsFirstTimeHomeBuyer(
			this.VM._client.id,
			checked,
		);
	}

	getSelectedSpouseText(
		clientId: string,
		spousalStatus: SpousalStatus,
		flaClient: PersonClient,
		allClients: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
	): string {
		switch (spousalStatus) {
			case SpousalStatus.SPOUSE_TO: {
				const spouse = flaClient || allClients?.find((x) => x.id === clientId);
				return `Spouse to ${(spouse as PersonClient)?.firstName} ${(spouse as PersonClient)?.lastName}`;
			}
			default:
				return SpousalText[spousalStatus];
		}
	}

	getSpousalItems(
		client: PersonClient,
		allClients: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
	): SpousalSelection[] {
		const items: SpousalSelection[] = [
			{
				name: SpousalText[SpousalStatus.NOT_A_SPOUSE],
				value: SpousalStatus.NOT_A_SPOUSE,
			},
		];

		const spousalChoices =
			allClients?.filter(
				(x) => x.type === ClientType.PERSON && x.id !== client?.id,
			) ?? [];

		spousalChoices.forEach((x) => {
			items.push({
				name: `Spouse to ${(x as PersonClient).firstName} ${(x as PersonClient).lastName}`,
				value: SpousalStatus.SPOUSE_TO,
				clientId: x.id,
			});
		});

		return items;
	}

	onClientSelected($event: Client): void {
		this.matterClientDispatch.SetSpousalClientId(
			this.VM._client.id,
			$event.id,
			$event.type === ClientType.PERSON && $event.leap?.cardId !== null,
		);
	}

	showCreateNewClient(clientCreationData: ClientCreationData): void {
		this.matterClientDispatch.CreateAndAddClientSpousalRelationshipToMatter(
			clientCreationData.client,
			this.VM._client.id,
		);
	}

	async offerorChecked(checked: boolean) {
		this.VM = {
			checkboxProcessing: true,
		};
		if (checked) {
			await this.matterClientsState.repo.OverwriteFields(
				this.VM._client.id,
				'offeror',
				clientLegalName(this.VM._client, this.nameFormattingSettings),
			);

			await this.matterClientsState.repo.AddToArray(
				this.VM._client.id,
				'clientTypes',
				MatterClientType.OFFEROR,
			);
		} else {
			if (!this.VM.isPurchaser && this.VM.checkboxProcessing) return;
			await this.matterClientsState.repo.OverwriteFields(
				this.VM._client.id,
				'offeror',
				null,
			);

			await this.matterClientsState.repo.RemoveFromArray(
				this.VM._client.id,
				'clientTypes',
				MatterClientType.OFFEROR,
			);
		}
		this.VM = {
			checkboxProcessing: false,
		};
	}

	async isPurchaserChecked(checked: boolean) {
		this.VM = {
			checkboxProcessing: true,
		};
		if (checked) {
			await this.matterClientsState.repo.AddToArray(
				this.VM._client.id,
				'clientTypes',
				MatterClientType.BUYER,
			);
		} else {
			if (!this.VM.showOfferor && this.VM.checkboxProcessing) return;
			await this.matterClientsState.repo.RemoveFromArray(
				this.VM._client.id,
				'clientTypes',
				MatterClientType.BUYER,
			);
		}
		this.VM = {
			checkboxProcessing: false,
		};
	}

	onSpouseKebabSelect(
		$event: DropdownResult<DropdownOption<ClientKebabOptionTypes>>,
	) {
		switch ($event.result.value) {
			case ClientKebabOptionTypes.GLOBALVIEW:
				this.drawerService.openDrawer(GlobalProfileComponent, {
					title: 'Global Profile',
					data: { clientId: this.VM.flaClient.id },
				});
				break;
			case ClientKebabOptionTypes.REMOVE:
				this.matterClientDispatch.SetSpousalClientId(this.VM._client.id, null);
				break;
		}
	}
}
